import React, {useCallback, useEffect, useState} from "react";
import {inject, observer} from "mobx-react";
import {useTranslation} from "react-i18next";
import {useNavigate, useParams} from "react-router-dom";
import {Layout} from "components/UI/layout";
import {ButtonTransparent} from "components/buttons/ButtonTransparent";
// local
import {IconUploadContract, IconChevronLeft, IconTrashCan} from "components/icons";
import {Form, Formik} from "formik";
import FormikField from "components/FormikField";
import EditDatePicker from "components/EditDatePicker";
import {TitleIcon} from "components/TitleIcon";
import {getOrganizationForSignContractMember} from "pages/SignContracts/api/contracts";
import ProjectProcurementSelect from "components/selects/ProjectProcurementSelect";
import LoadDocsColumn from "components/LoadDocsColumn";
import {IconContractDocuments} from "components/icons";
import {CONTRACTS, toUploadedContractId} from "constants/routes.const";
import {showSuccess} from "helpers/notifications.helper";
import {uploadFiles, removeFilesConfirmModal} from "helpers/helper";
import {ConfirmModal} from "components/ConfirmModal";
import {color} from "constants/color.consts";
import {Preloader} from "components/Preloader";
import {schema, initialValues} from "./components/formAttrs";
import {SupplierField} from "./components/SupplierField";

const baseLangKey = "UPLOADED_CONTRACT_";

const UploadedContractPage = inject("store")(
  observer(({store: {contractsStore}}) => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {contractId} = useParams();
    const isNew = contractId === "create";

    const [loading, setLoading] = useState(true);
    const [contract, setContract] = useState(initialValues);
    const [selectedProject, setSelectedProject] = useState(null);
    const [selectedProcurement, setSelectedProcurement] = useState(null);
    const [uploadingFiles, setUploadingFiles] = useState(false);

    const onBackToContracts = useCallback(() => navigate(CONTRACTS), [navigate]);

    const fetchUploadedContract = useCallback(async () => {
      if (isNew) return setLoading(false);
      contractsStore
        .getUploadedContract(contractId)
        .then((data) => {
          setContract(data);
          data.ProjectId && setSelectedProject(data.Project);
          if (data.ProjectItemId) setSelectedProcurement(data.ProjectItem);
        })
        .finally(() => setLoading(false));
    }, [contractId, contractsStore, isNew]);

    const addFileHandler = (values, setValues, docs) =>
      uploadFiles(
        values,
        contractsStore.getFileUID,
        (data) => setValues("documents", [...docs, ...data.map((item) => item.File)]),
        setUploadingFiles
      );

    const removeFileHandler = (id, setValues, docs) =>
      removeFilesConfirmModal(id, (id) => removeDocument(id, setValues, docs));

    const removeDocument = (id, setValues, docs) => {
      setValues("documents", id ? docs.filter((item) => item?.id !== id) : []);
      showSuccess(t(id ? "FILE_DELETED" : "ALL_FILES_DELETED"));
    };

    const handleProjectChange = (value, setValue) => {
      setValue("ProjectId", value?.id ?? null);
      setValue("ProjectItemId", null);
      setSelectedProject(value);
      !value && setSelectedProcurement(null);
    };

    const handleProcurementChange = (value, setValue) => {
      setValue("ProjectItemId", value?.id ?? null);
      setSelectedProcurement(value);
    };

    const handleSubmit = (values) => {
      if (isNew)
        contractsStore
          .createUploadedContract(values)
          .then((data) => navigate(toUploadedContractId(data.id), {replace: true}))
          .finally(() => setLoading(false));
      else
        contractsStore
          .updateUploadedContract(contractId, values)
          .then((data) => {
            showSuccess(t("UPDATED"));
            setContract(data);
          })
          .finally(() => setLoading(false));
    };

    const confirmDelete = () =>
      ConfirmModal({
        title: t("UPLOADED_CONTRACT_WANT_DELETE_CONTRACT"),
        text: t("UPLOADED_CONTRACT_CANT_RETURN"),
        type: "warning",
        onOk: onDeleteContract,
      });
    const onDeleteContract = () => {
      setLoading(true);
      contractsStore
        .deleteUploadedContract(contractId)
        .then(() => {
          onBackToContracts();
          showSuccess(t("UPLOADED_CONTRACT_CONTRACT_DELETED"));
        })
        .finally(() => setLoading(false));
    };

    useEffect(() => {
      fetchUploadedContract();
    }, [fetchUploadedContract]);

    return (
      <Layout>
        {loading ? (
          <Preloader addClass="preloader500" />
        ) : (
          <React.Fragment>
            <div className="bg-white pa-2 pl-4">
              <ButtonTransparent
                name={`${baseLangKey}BUTTON_BACK`}
                icon={<IconChevronLeft color={color.blue} />}
                addClass="pl-0"
                bold
                noBorder
                uppercase
                onClick={onBackToContracts}
              />
            </div>
            <div className="uploaded-contracts-content">
              <TitleIcon
                title={t(`${baseLangKey}TITLE`)}
                medium
                uppercase={false}
                addClass="mb-8"
                icon={<IconUploadContract color={color.black} />}
              />
              <Formik
                initialValues={{...contract}}
                enableReinitialize
                validationSchema={schema}
                onSubmit={handleSubmit}
              >
                {({values, setFieldValue, isValid, dirty}) => (
                  <Form className="full-width">
                    <div className="df-row">
                      <FormikField
                        name="name"
                        autoFocus
                        label={t(`${baseLangKey}NAME_FIELD_LABEL`)}
                        addClassLabel="fz-12 __bold __uppercase"
                        placeholder={t(`${baseLangKey}NAME_FIELD_PLACEHOLDER`)}
                        small
                        addClass="fg2 mr-4"
                        className="input"
                        highlightIfEmpty={!values.name}
                      />
                      <FormikField
                        name="number"
                        label={t(`${baseLangKey}NUMBER_FIELD_LABEL`)}
                        width="200px"
                        addClassLabel="fz-12 __bold __uppercase"
                        placeholder={t(`${baseLangKey}NUMBER_FIELD_PLACEHOLDER`)}
                        small
                        className="input"
                      />
                    </div>
                    <div className="df-row mb-4">
                      <EditDatePicker
                        label={t(`${baseLangKey}DATE_FIELD_LABEL`)}
                        name="date"
                        small
                        boldLabel
                        uppercaseLabel
                        hideErrorMessage
                        addClassLabel="fz-12 __bold __uppercase"
                        addClassInput="hght-38"
                        highlightIfEmpty={!values.date}
                        placeholder={t(`${baseLangKey}DATE_FIELD_PLACEHOLDER`)}
                      />
                      <SupplierField
                        name="supplierName"
                        label={`${baseLangKey}SUPPLIER_FIELD_LABEL`}
                        placeholder={`${baseLangKey}SUPPLIER_FIELD_PLACEHOLDER`}
                        highlightIfEmpty={!values.supplierName}
                        addClass="fg2"
                        hideErrorMessage
                        searchOrganizations={(value) =>
                          getOrganizationForSignContractMember(value, undefined)
                        }
                      />
                    </div>
                    <div className="mb-6">
                      <TitleIcon
                        title={t(`${baseLangKey}PROJECT_AND_RFT_FIELD_LABEL`)}
                        small
                        addClass="mb-2"
                      />
                      <ProjectProcurementSelect
                        selectedProject={selectedProject}
                        selectedProcurement={selectedProcurement}
                        onProjectChange={(project) =>
                          handleProjectChange(project, setFieldValue)
                        }
                        onProcurementChange={(procurement) =>
                          handleProcurementChange(procurement, setFieldValue)
                        }
                      />
                    </div>

                    <LoadDocsColumn
                      title={`${baseLangKey}DOCUMENTS_FIELD_LABEL`}
                      docPlaceholder={`${baseLangKey}DOCUMENTS_FIELD_PLACEHOLDER`}
                      titleIcon={<IconContractDocuments />}
                      docs={values.documents}
                      button="ADD"
                      editable
                      highlightIfEmpty
                      addFile={(files) =>
                        addFileHandler(files, setFieldValue, values.documents)
                      }
                      canRemoveAllFiles
                      removeFile={(id) =>
                        removeFileHandler(id, setFieldValue, values.documents)
                      }
                      loading={uploadingFiles}
                      multiple={true}
                      fullWidth
                      addClass="mb-6"
                    />
                    <FormikField
                      name="notes"
                      label={t(`${baseLangKey}NOTES_FIELD_LABEL`)}
                      placeholder={t(`${baseLangKey}NOTES_FIELD_PLACEHOLDER`)}
                      addClassLabel="fz-12 __bold __uppercase"
                      autoSize={{maxRows: 10}}
                      maxLength="1000"
                      textarea
                      height="100px"
                      className="pa-2"
                      textareaValue={values.notes}
                    />
                    <div className="df-row-center mt-6">
                      {!isNew && (
                        <ButtonTransparent
                          icon={<IconTrashCan />}
                          iconButton
                          color="red"
                          type="button"
                          disabled={uploadingFiles}
                          loading={loading}
                          onClick={confirmDelete}
                        />
                      )}
                      <ButtonTransparent
                        name="CANCEL"
                        type="button"
                        color="red"
                        medium
                        onClick={onBackToContracts}
                        disabled={uploadingFiles}
                        loading={loading}
                        addClass="mla"
                      />
                      <ButtonTransparent
                        name={`${baseLangKey}${isNew ? "SAVE" : "UPDATE"}_BUTTON`}
                        medium
                        type="submit"
                        addClass="ml-4"
                        disabled={uploadingFiles || !isValid || !dirty}
                        loading={loading}
                      />
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </React.Fragment>
        )}
      </Layout>
    );
  })
);

export default UploadedContractPage;
